/**
 * フロントエンドエラーハンドリングユーティリティ
 */

export interface ApiError {
  error: string;
  type: string;
  errorId: string;
  timestamp: string;
  fields?: Record<string, string>;
  stripe_code?: string;
  requires_action?: boolean;
  retryAfter?: number;
}

export interface ProcessedError {
  userMessage: string;
  technicalMessage: string;
  errorType: string;
  shouldRetry: boolean;
  retryAfter?: number;
  requiresReauth?: boolean;
}

export class ErrorHandler {
  /**
   * APIエラーレスポンスを処理
   */
  static processApiError(error: any): ProcessedError {
    // ネットワークエラー
    if (!error.response) {
      return {
        userMessage: 'インターネット接続を確認してください。',
        technicalMessage: error.message || 'Network error',
        errorType: 'network_error',
        shouldRetry: true
      };
    }

    const { status, data } = error.response;
    const apiError: ApiError = data;

    // ステータスコード別の処理
    switch (status) {
      case 400:
        return this.processBadRequestError(apiError);
      case 401:
        return this.processUnauthorizedError(apiError);
      case 403:
        return this.processForbiddenError(apiError);
      case 404:
        return this.processNotFoundError(apiError);
      case 409:
        return this.processConflictError(apiError);
      case 429:
        return this.processRateLimitError(apiError);
      case 500:
        return this.processServerError(apiError);
      default:
        return this.processGenericError(status, apiError);
    }
  }

  private static processBadRequestError(apiError: ApiError): ProcessedError {
    if (apiError.type === 'validation_error') {
      return {
        userMessage: apiError.error || '入力データに問題があります。',
        technicalMessage: `Validation error: ${JSON.stringify(apiError.fields)}`,
        errorType: 'validation_error',
        shouldRetry: false
      };
    }

    if (apiError.type === 'stripe_error') {
      return this.processStripeError(apiError);
    }

    return {
      userMessage: apiError.error || '入力データに問題があります。',
      technicalMessage: apiError.error || 'Bad request',
      errorType: 'bad_request',
      shouldRetry: false
    };
  }

  private static processUnauthorizedError(apiError: ApiError): ProcessedError {
    const messages = {
      'TOKEN_EXPIRED': 'リンクの有効期限が切れています。新しいリンクをリクエストしてください。',
      'TOKEN_NOT_FOUND': '決済情報が見つかりません。',
      'INVALID_TOKEN': 'トークンが無効です。'
    };

    const userMessage = messages[apiError.type as keyof typeof messages] || 
                       apiError.error || 
                       '認証に失敗しました。';

    return {
      userMessage,
      technicalMessage: apiError.error || 'Unauthorized',
      errorType: apiError.type || 'unauthorized',
      shouldRetry: false,
      requiresReauth: true
    };
  }

  private static processForbiddenError(apiError: ApiError): ProcessedError {
    return {
      userMessage: 'このリソースにアクセスする権限がありません。',
      technicalMessage: apiError.error || 'Forbidden',
      errorType: 'forbidden',
      shouldRetry: false
    };
  }

  private static processNotFoundError(apiError: ApiError): ProcessedError {
    return {
      userMessage: '決済情報が見つかりません。リンクを確認してください。',
      technicalMessage: apiError.error || 'Not found',
      errorType: 'not_found',
      shouldRetry: false
    };
  }

  private static processConflictError(apiError: ApiError): ProcessedError {
    if (apiError.type === 'TOKEN_ALREADY_USED') {
      return {
        userMessage: 'このリンクは既に使用済みです。',
        technicalMessage: 'Token already used',
        errorType: 'token_already_used',
        shouldRetry: false
      };
    }

    return {
      userMessage: apiError.error || 'データの競合が発生しました。',
      technicalMessage: apiError.error || 'Conflict',
      errorType: 'conflict',
      shouldRetry: false
    };
  }

  private static processRateLimitError(apiError: ApiError): ProcessedError {
    const retryAfter = apiError.retryAfter ? new Date(apiError.retryAfter * 1000) : null;
    const retryMessage = retryAfter ? 
      `${retryAfter.toLocaleString()}以降に再試行してください。` : 
      'しばらく待ってから再試行してください。';

    return {
      userMessage: `リクエスト回数の制限に達しました。${retryMessage}`,
      technicalMessage: apiError.error || 'Rate limit exceeded',
      errorType: 'rate_limit_exceeded',
      shouldRetry: true,
      retryAfter: apiError.retryAfter
    };
  }

  private static processServerError(apiError: ApiError): ProcessedError {
    return {
      userMessage: 'サーバーエラーが発生しました。しばらく待ってから再試行してください。',
      technicalMessage: apiError.error || 'Internal server error',
      errorType: 'server_error',
      shouldRetry: true
    };
  }

  private static processStripeError(apiError: ApiError): ProcessedError {
    const stripeMessages: Record<string, string> = {
      'card_declined': 'カードが拒否されました。別のカードをお試しください。',
      'insufficient_funds': '残高不足です。別のカードをお試しください。',
      'incorrect_cvc': 'セキュリティコード（CVC）が正しくありません。',
      'expired_card': 'カードの有効期限が切れています。',
      'processing_error': 'カード処理でエラーが発生しました。しばらく待ってから再試行してください。'
    };

    const userMessage = apiError.stripe_code ? 
      stripeMessages[apiError.stripe_code] || apiError.error :
      apiError.error || '決済処理でエラーが発生しました。';

    return {
      userMessage,
      technicalMessage: `Stripe error: ${apiError.stripe_code} - ${apiError.error}`,
      errorType: 'stripe_error',
      shouldRetry: apiError.stripe_code === 'processing_error',
      requiresReauth: apiError.requires_action
    };
  }

  private static processGenericError(status: number, apiError: ApiError): ProcessedError {
    return {
      userMessage: apiError.error || `エラーが発生しました (${status})`,
      technicalMessage: `HTTP ${status}: ${apiError.error || 'Unknown error'}`,
      errorType: 'generic_error',
      shouldRetry: status >= 500
    };
  }

  /**
   * Stripe client-side エラーを処理
   */
  static processStripeClientError(stripeError: any): ProcessedError {
    const errorMessages: Record<string, string> = {
      'card_error': 'カード情報に問題があります。',
      'validation_error': '入力情報を確認してください。',
      'api_connection_error': 'ネットワーク接続に問題があります。',
      'api_error': '決済サービスでエラーが発生しました。',
      'authentication_error': '認証に失敗しました。',
      'rate_limit_error': 'リクエストが多すぎます。しばらく待ってください。'
    };

    const userMessage = errorMessages[stripeError.type] || 
                       stripeError.message || 
                       '決済処理でエラーが発生しました。';

    return {
      userMessage,
      technicalMessage: `Stripe client error: ${stripeError.type} - ${stripeError.message}`,
      errorType: 'stripe_client_error',
      shouldRetry: ['api_connection_error', 'rate_limit_error'].includes(stripeError.type),
      requiresReauth: stripeError.type === 'authentication_error'
    };
  }

  /**
   * エラーログの記録
   */
  static logError(error: ProcessedError, context: string, additionalData?: any) {
    const logData = {
      timestamp: new Date().toISOString(),
      context,
      errorType: error.errorType,
      userMessage: error.userMessage,
      technicalMessage: error.technicalMessage,
      additionalData
    };

    // 開発環境でのコンソール出力
    if (process.env.NODE_ENV === 'development') {
      console.error('Error logged:', logData);
    }

    // 本番環境では外部ログサービスに送信
    // if (process.env.NODE_ENV === 'production') {
    //   // 外部ログサービス（DataDog、CloudWatch等）に送信
    // }
  }
}